<script setup lang="ts" generic="TableData extends Api.Common.CommonRecord">
import type { Ref, VNode } from 'vue';
import { h, ref, toValue, watch } from 'vue';
import { NSwitch } from 'naive-ui';
import dayjs from 'dayjs';
import { cloneDeep } from 'lodash-es';
import _ from 'lodash';
import { ShowWhenDisabledComponents } from '@/constants/common';
import { useNaiveForm } from '@/hooks/common/form';
import { $t } from '@/locales';
import type { SearchFieldSchema } from '@/components/advanced/basic-table/use-table';
import type { Api } from '@/typings/api';

defineOptions({
  name: 'TableSearch'
});

const props = withDefaults(
  defineProps<{
    searchFields: SearchFieldSchema<TableData>[];
    // eslint-disable-next-line vue/no-unused-properties
    labelWidth?: number;
  }>(),
  {
    labelWidth: 90
  }
);

const emit = defineEmits<{
  // reset: [filters: Api.Common.Filter[]];
  search: [filters: Api.Common.Filter[]];
}>();

const { formRef, validate, restoreValidation } = useNaiveForm();
const model: Ref<Record<string, Api.Common.Filter>> = ref({});

watch(
  () => props.searchFields.length,
  () => {
    initModel();
    emit('search', getSearchFilters());
  },
  {
    immediate: true
  }
);

function initModel() {
  props.searchFields.forEach(field => {
    model.value[field.key] = {
      column: field.key,
      type: field.searchType,
      autoSearch: field.autoSearch,
      value:
        // eslint-disable-next-line no-nested-ternary
        'defaultValue' in field && field.defaultValue
          ? field.defaultValue
          : // eslint-disable-next-line no-nested-ternary
            ['andIn', 'andNotIn'].includes(field.searchType)
            ? []
            : null
    } as Api.Common.Filter;
  });
}
/**
 * 获取精确匹配或模糊匹配的后缀组件
 *
 * @param searchField searchField
 * @param fiterType
 */
function getExactSwitchSuffix(searchField: SearchFieldSchema<TableData>, filterType: Api.Common.FilterType): VNode {
  return h(
    NSwitch,
    {
      defaultValue: filterType,
      round: false,
      style: {
        position: 'absolute',
        right: '5px',
        top: '8px'
      },
      size: 'small',
      checkedValue: 'andEq',
      uncheckedValue: 'andLike',
      onUpdateValue: (value: string) => {
        searchField.searchType = value as Api.Common.FilterType;
        model.value[searchField.key].type = value as Api.Common.FilterType;
      }
    },
    {
      checked: () => h('span', '='),
      unchecked: () => h('span', '.*')
    }
  );
}

async function reset() {
  initModel();
  await restoreValidation();
  emit('search', getSearchFilters());
}

function getSearchFilters(): Api.Common.Filter[] {
  const searchFilters = Object.values(cloneDeep(model.value)).filter(
    filter =>
      filter.value !== null &&
      (typeof filter.value === 'string' ? filter.value.trim() !== '' : true) &&
      (Array.isArray(filter.value) ? filter.value.length > 0 : true)
  );
  if (searchFilters.length > 0) {
    searchFilters.forEach(filter => {
      if (typeof filter.value === 'string') {
        filter.value = filter.value.trim();
      }
      switch (filter.type) {
        case 'andLike':
          filter.value = `%${filter.value}%`;
          break;
        case 'andDateRange':
        case 'andMonthRange':
        case 'andYearRange':
          if (Array.isArray(filter.value) && filter.value.length === 2) {
            const dateType =
              // eslint-disable-next-line no-nested-ternary
              filter.type === 'andDateRange' ? 'day' : filter.type === 'andMonthRange' ? 'month' : 'year';
            const newFilter: Api.Common.Filter = {
              type: 'and',
              column: null,
              value: [
                {
                  type: 'andGte',
                  column: filter.column,
                  value: dayjs(filter.value[0]).startOf(dateType).format('YYYY-MM-DD HH:mm:ss')
                },
                {
                  type: 'andLt',
                  column: filter.column,
                  value: dayjs(filter.value[1]).add(1, dateType).startOf(dateType).format('YYYY-MM-DD HH:mm:ss')
                }
              ]
            };
            Object.assign(filter, newFilter);
          }
          break;
        case 'andRange':
          if (Array.isArray(filter.value) && filter.value.length === 2) {
            const newFilter: Api.Common.Filter = {
              type: 'and',
              column: null,
              value: [
                {
                  type: 'andGte',
                  column: filter.column,
                  value: filter.value[0]
                },
                {
                  type: 'andLte',
                  column: filter.column,
                  value: filter.value[1]
                }
              ]
            };
            Object.assign(filter, newFilter);
          }
          break;
        default:
      }
    });
  }
  return searchFilters;
}

async function search() {
  await validate();
  emit('search', getSearchFilters());
}

const throttledSearch = _.throttle(() => {
  search();
}, 1500);

function handleAutoSearch(autoChange: boolean | undefined) {
  if (autoChange) {
    throttledSearch();
  }
}
</script>

<template>
  <NCard :title="$t('common.search')" :bordered="false" size="small" class="card-wrapper">
    <template #header-extra>
      <NSpace class="w-full" justify="end">
        <NButton @click="reset">
          <template #icon>
            <icon-ic-round-refresh class="text-icon" />
          </template>
          重置
        </NButton>
        <NButton type="primary" ghost @click="search">
          <template #icon>
            <icon-ic-round-search class="text-icon" />
          </template>
          搜索
        </NButton>
      </NSpace>
    </template>
    <NForm ref="formRef" :model="model" label-placement="left" :label-width="props.labelWidth">
      <NGrid responsive="screen" x-gap="12px" item-responsive>
        <template v-for="field in searchFields" :key="field.key">
          <NFormItemGi
            v-if="model[field.key]"
            :span="field.colSpan ? field.colSpan : '24 s:12 m:8'"
            :label="field.title"
            :path="field.key"
          >
            <DynamicElement
              v-model:value="model[field.key].value"
              :component="field.component?.type"
              :v-model-field="field.vModelField"
              :component-props="field.component?.props"
              :component-slots="toValue(field.component?.slots as Record<string, VNode>)"
              :register="field.component?.register"
              :help-message="field.helpMessage"
              :disabled="field.disabled"
              :show-component-when-disabled="ShowWhenDisabledComponents.includes(field.component?.type)"
              :suffix="field.showExactSwitch ? getExactSwitchSuffix(field, field.searchType) : null"
              @submit="search"
              @change="handleAutoSearch(model[field.key].autoSearch)"
            ></DynamicElement>
          </NFormItemGi>
        </template>
      </NGrid>
    </NForm>
  </NCard>
</template>

<style scoped></style>
